/**
 * CUSTOMER FIX Stage 7 — subscribe to persisted multi-cart registry.
 */

import { useSyncExternalStore } from 'react';
import {
  CUSTOMER_CART_REGISTRY_CHANGED_EVENT,
  CART_REGISTRY_STORAGE_KEY,
  countNonemptyTenantCarts,
  hasAnyNonemptyPersistedCart,
  listNonemptyCartRegistryRows,
  type CartRegistryRow,
} from '../features/shop/cart/cartRegistry.js';
import { CUSTOMER_SHOP_CART_CHANGED_EVENT } from '../features/shop/cart/cartStorage.js';

let registryEpoch = 0;
const registryListeners = new Set<() => void>();

function bumpRegistry(): void {
  registryEpoch += 1;
  for (const listener of registryListeners) {
    listener();
  }
}

function subscribeRegistry(onStoreChange: () => void): () => void {
  registryListeners.add(onStoreChange);
  const onEvent = (): void => {
    bumpRegistry();
  };
  const onStorage = (event: StorageEvent): void => {
    if (event.storageArea !== window.localStorage) {
      return;
    }
    if (
      event.key === null ||
      event.key === CART_REGISTRY_STORAGE_KEY ||
      event.key.startsWith('rpapp-customer.shop.cart:')
    ) {
      bumpRegistry();
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener(CUSTOMER_CART_REGISTRY_CHANGED_EVENT, onEvent);
    window.addEventListener(CUSTOMER_SHOP_CART_CHANGED_EVENT, onEvent);
    window.addEventListener('storage', onStorage);
  }
  return () => {
    registryListeners.delete(onStoreChange);
    if (typeof window !== 'undefined') {
      window.removeEventListener(CUSTOMER_CART_REGISTRY_CHANGED_EVENT, onEvent);
      window.removeEventListener(CUSTOMER_SHOP_CART_CHANGED_EVENT, onEvent);
      window.removeEventListener('storage', onStorage);
    }
  };
}

function getRegistrySnapshot(): number {
  return registryEpoch;
}

export function useMultiCartRegistryRows(): readonly CartRegistryRow[] {
  const epoch = useSyncExternalStore(
    subscribeRegistry,
    getRegistrySnapshot,
    () => 0,
  );
  void epoch;
  return listNonemptyCartRegistryRows();
}

export function useNonemptyTenantCartCount(): number {
  const epoch = useSyncExternalStore(
    subscribeRegistry,
    getRegistrySnapshot,
    () => 0,
  );
  void epoch;
  return countNonemptyTenantCarts();
}

export function useHasAnyNonemptyPersistedCart(): boolean {
  const epoch = useSyncExternalStore(
    subscribeRegistry,
    getRegistrySnapshot,
    () => 0,
  );
  void epoch;
  return hasAnyNonemptyPersistedCart();
}

/** @internal tests */
export function __resetMultiCartRegistryStoreForTests(): void {
  registryEpoch = 0;
  registryListeners.clear();
}
